if (!customElements.get('form-component')) {
  customElements.define('form-component',
    class FormComponent extends HTMLElement {
      constructor() {
        super();

        try {
          const eli18nJsonNode = this.querySelector('[data-settings]');
          const eli18nJsonText = eli18nJsonNode.textContent;
          this.settings = JSON.parse(eli18nJsonText);
        } catch (error) {
          this.settings = {};
        }
        
        this.elements = {
          title: this.querySelector('h2.title'),
          form: this.querySelector('form'),
          inputs: this.querySelectorAll('input:not([type="hidden"]), select, textarea'),
          submitButton: this.querySelector('button[type="submit"]'),
          successMessage: document.querySelector(`modal-dialog[id$="${this.querySelector('form').dataset.formIdentifier}-form-success-message"]`),
          errorMessage: this.querySelector(`#${this.querySelector('form').dataset.formIdentifier}-form-error-message`),
          description: this.querySelector('.form__description'),
          acceptTerms: this.querySelector('input[name="accept-terms"]'),
          select: this.querySelectorAll('select.select__select'),
          recaptcha: this.querySelector("[data-recaptcha-element]"),
        };
        this.isValid = true;
      }
      
      connectedCallback() {
        this.elements.form.addEventListener('submit', this.onSubmit.bind(this));
        this.elements.acceptTerms.addEventListener('change', this.onTermsChange.bind(this));
        this.elements.select.forEach((select) => select.addEventListener('change', this.onSelectChange.bind(this)));
      }

      createFormSubmitData(formData) {
        /**
         * Build a new form data object with the original form data nested under the key "form" 
         * and the files as an array under the key "files".
         */
        const originalFormData = formData;
        const newFormData = new FormData();

        const fileInputs = [...this.elements.inputs].filter(input => input.type === 'file');
        const files = fileInputs.filter(input => input.files[0]).map(input => input.files[0]);

        fileInputs.forEach((fileInput) => {
          originalFormData.delete(fileInput.name);
          if (fileInput.files.length > 0) {
            newFormData.append(`form[file_${fileInput.name}]`, fileInput.files[0].name);
          }
        });

        const multiOptionInputs = [...this.elements.inputs].filter(input => input.type === 'checkbox' && input.name.includes('[') && input.name.includes(']') && input.checked);
        const optionMap = new Map();

        multiOptionInputs.forEach((multiOptionInput) => {
          originalFormData.delete(multiOptionInput.name);
          const name = multiOptionInput.name.split('[')[0];
          if (optionMap.has(name)) {
            optionMap.set(name, [...optionMap.get(name), multiOptionInput.value]);
          } else {
            optionMap.set(name, [multiOptionInput.value]);
          }
        });

        optionMap.forEach((value, key) => {
          newFormData.append(`form[${key}]`, JSON.stringify(value));
        });

        for (const entry of originalFormData.entries()) {
          newFormData.append(`form[${entry[0]}]`, entry[1]);
        }

        files.forEach((file) => newFormData.append('form[files][]', file));

        return newFormData;
      }
      
      async onSubmit(event) {
        event.preventDefault();
        
        this.resetFormState();
        this.setButtonState('busy');
        this.elements.inputs.forEach(input => this.validateField(input));
        
        if (!this.isValid) {
          this.querySelector('[aria-invalid="true"]')?.focus();
          this.setButtonState('idle');
          return;
        }
        
        const originalFormData = new FormData(this.elements.form);
        if (this.elements.form.dataset.testMode === 'false') {
          const res = await fetch("/a/portal/api/v1/forms", {
            method: "POST",
            body: this.createFormSubmitData(originalFormData)
          });

          if (!res.ok) {
            this.querySelector('[aria-invalid="true"]')?.focus();
            this.setButtonState('idle');

            try {
              const contentType = res.headers.get('Content-Type');
              if (contentType && contentType.includes('application/json')) {
                const errorMessage = await res.json();
                if (errorMessage.errors && errorMessage.errors.length > 0) {
                  return this.showErrorMessage(errorMessage.errors);
                }
              }
            } catch (error) {
              console.error(error);
            }

            return this.showErrorMessage();
          }
        }

        this.clearErrorMessage();
        this.trackFormSubmit(originalFormData);
        this.showSuccessMessage(originalFormData);
      }

      onTermsChange(e) {
        if (this.elements.form.dataset.useRecaptcha === "false") {
          if (!e.target.checked) {
            e.target.checked = true;
          }

          this.elements.submitButton.removeAttribute('disabled');

          return e.target.checked;
        } else {
          if (this.termsAccepted) {
            e.preventDefault();
            e.target.checked = this.termsAccepted;
            return e.target.checked;
          }
          if (e.target.checked) {
            this.termsAccepted = true;

            /**
             * Load reCAPTCHA script if not already loaded.
             * Can be tested if grecaptcha function is not already globally available.
             */
            if (!window.grecaptcha) {
              const recaptchaScript = document.createElement('script');
              recaptchaScript.async = true;
              recaptchaScript.defer = true;
              recaptchaScript.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
              const firstScript = document.getElementsByTagName('script')[0];
              firstScript.parentNode.insertBefore(recaptchaScript,firstScript);
            }

            /**
             * We can not use the callback function provided by Google.
             * Every reCAPTCHA needs to be loaded isolated, so that also callback functions run isolated.
             */
            this.asyncFunction("grecaptcha", () => {
              /**
               * We create a dedicated id, so that we can create isolated functions which are dispatched by reCAPTCHA.
               */
              const functionsId = this.hash(10);
              
              /**
               * We define the callback functions with the dedicated id
               */
              window[`onSuccessCallback_${functionsId}`] = (e) => this.elements.submitButton.removeAttribute('disabled');
              window[`onExpiredCallback_${functionsId}`] = (e) => this.elements.submitButton.setAttribute('disabled', 'disabled');
              window[`onErrorCallback_${functionsId}`] = (e) => this.elements.submitButton.setAttribute('disabled', 'disabled');

              /**
               * Render the reCATPCHA element, bound and isolated within the form
               */
              grecaptcha.render(this.elements.recaptcha, {
                'sitekey' : '6LcswiYqAAAAAGGfp_jSUmtnJwublpp5N9HvLwCJ',
                'callback': `onSuccessCallback_${functionsId}`,
                'expired-callback': `onExpiredCallback_${functionsId}`,
                'error-callback	': `onErrorCallback_${functionsId}`,
              });
            });
          }
        }
      }

      onSelectChange(e) {
        e.target.classList.toggle("select__select--is-selected", e.target.value !== "");
      }

      showErrorMessage(errors = []) {
        this.elements.errorMessage.innerHTML =
          `<div role="alert" aria-live="assertive" aria-atomic="true">
            ${errors.length > 0
              ? `<p>${this.settings.i18n["scp_forms.error_message"]}</p><ul>${errors.map(error => `<li>${error}</li>`).join('')}</ul>`
              : this.settings.i18n["scp_forms.error_message"]}
          </div>`
      }

      clearErrorMessage() {
        this.elements.errorMessage.innerHTML = '';
      }
      
      showSuccessMessage(formData) {
        this.elements.successMessage.querySelector('.modal-component__content').innerHTML = this.dynamicReplace(this.elements.form.dataset.successMessage, formData);
        this.elements.successMessage.setAttribute('open', '');
        this.setButtonState('idle');
        
        this.elements.successMessage.querySelector('.modal-component__body').focus();
        if (this.elements.successMessage.querySelector('[data-close-modal], .modal-component__toggle')) {
          this.elements.successMessage.querySelectorAll('[data-close-modal], .modal-component__toggle').forEach((el) => {
            const controller = new AbortController();

            el.addEventListener('click', (e) => {
              e.currentTarget.closest('modal-dialog')?.hide();
              this.elements.form.closest('modal-dialog')?.hide();
              this.clearForm();
              controller.abort();
            }, { signal: controller.signal });

            window.addEventListener('keydown', (e) => {
              if (e.key === 'Escape') {
                if (e && e.currentTarget && typeof e.currentTarget.closest == 'function' ) {
                  e?.currentTarget?.closest('modal-dialog')?.hide();
                }
                this.elements.form.closest('modal-dialog')?.hide();
                this.clearForm();
              }
            }, { signal: controller.signal });
          });
        }
      }
      
      setButtonState(state = 'idle') {
        if (state === 'busy') {
          this.elements.submitButton.setAttribute('disabled', 'true');
          this.elements.submitButton.setAttribute('aria-disabled', 'true');
          this.elements.submitButton.setAttribute('aria-busy', 'true');
          this.elements.submitButton.classList.add('loading');
        } else {
          this.elements.submitButton.removeAttribute('disabled');
          this.elements.submitButton.removeAttribute('aria-disabled');
          this.elements.submitButton.removeAttribute('aria-busy');
          this.elements.submitButton.classList.remove('loading');
        }
      }
      
      clearForm() {
        this.resetFormState();
        this.clearErrorMessage();
        this.elements.inputs.forEach(input => {
          const defaultValue = input.getAttribute('data-default');
          if (input.type === 'checkbox' || input.type === 'radio') {
            input.checked = defaultValue ?? false;
          } else {
            input.value = defaultValue ?? '';
          }
        });
        [...this.querySelectorAll('language-select')].forEach((el) => {
          el.dispatchEvent(new CustomEvent('form:clear'));
        });
        [...this.querySelectorAll('conditional-formsection')].forEach((el) => {
          el.dispatchEvent(new CustomEvent('form:clear'));
        })
        this.elements.acceptTerms.checked = false;
        grecaptcha.reset();
      }
      
      resetFormState() {
        this.isValid = true;
        this.querySelectorAll('.field__error').forEach(element => element.innerText = '');
        this.elements.inputs.forEach(this.resetInputFieldError);
      }
      
      resetInputFieldError(field) {
        field?.closest('.field')?.classList.remove('field--error')
        field.setCustomValidity('');
        field.removeAttribute('aria-describedby');
        field.removeAttribute('aria-invalid');
      }
      
      createErrorMessage(field, errorMessage) {
        field.closest('.field').classList.add('field--error');
        
        if (errorMessage && field.closest('.field-wrapper').querySelector('.field__error')?.innerText === '') {
          const errorElement = this.querySelector(`#${this.elements.form.dataset.formIdentifier}-${field.name.split('[')[0]}-error`);
          if (errorElement) {
            errorElement.innerHTML = `<div role="alert" aria-live="assertive" aria-atomic="true">${errorMessage}</div>`
          }
          field.setAttribute('aria-describedby', errorElement?.id);
          field.setAttribute('aria-invalid', 'true');
          field.setCustomValidity(errorMessage);
        }
      }
      
      validateField(field) {
        const isRequired = field.required;
        const isValid = field.validity.valid;
        const isDisabled = field.disabled;
        
        if (!isValid && !isDisabled) {
          if (isRequired && (field.value === '' || (field.type === 'checkbox' && !field.checked) || (field.type === 'radio' && !field.checked))) {
            this.createErrorMessage(field, this.settings.i18n["scp_forms.field_is_required"]);
          } else {
            this.createErrorMessage(field, field.dataset.errorMessage);
          }
          this.isValid = false;
        }
      }
      
      dynamicReplace(content, formData) {
        const regex = /%form\.([\w\.]+)%/g;
        return content.replace(regex, (match, p1) => {
          const value = formData.get(p1);
          return value ? value : '';
        });
      }

      trackFormSubmit(formData) {

        try {
          const analyticsParams = {
            form_identifier: this.elements.form?.dataset?.formIdentifier || null,
            form_id: this.elements.form?.elements?.form_id?.value || null,
            form_language: this.elements.form?.elements?.locale?.value || null,
            form_name: this.elements.form?.elements?.form_name?.value || null,
            form_path: this.elements.form?.elements?.form_path?.value || null,
            form_recipient: this.elements.form?.elements?.recipient?.value || null,
            form_reference: this.elements.form?.elements?.form_reference?.value || null,
            page_url: window.location?.href || null,
            pick_your_cooking_show_experience: this.elements.form?.elements?.pick_your_cooking_show_experience?.value?.split('|')[1] || null,
            sales_consultant_external_id: this.elements.form?.elements?.sales_consultant_external_id?.value || null,
            test_mode: this.elements.form?.dataset?.testMode === 'true',
          };

          if (typeof window.gtag === 'function') {
            window.gtag('event', 'form_submit', analyticsParams);
          } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
            window.dataLayer.push(Object.assign({ event: 'form_submit' }, analyticsParams));
          }
        } catch (error) {
          // Non-fatal: analytics should never break the form flow
          console.error('Analytics dispatch error', error);
        }
      }

      asyncFunction(functionName, callback, retries = 10) {
        const RETRY_DELAY = 100
        let times = 1
      
        const track = setInterval(() => {
          if (times === retries) {
            clearInterval(track)
          } else if (typeof window[functionName] !== 'undefined') {
            if (typeof callback === 'function') {
              callback();
            }
            clearInterval(track)
          } else {
            times++
          }
        }, RETRY_DELAY);
      }

      hash(length) {
        return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, c => {
          const r = Math.random() * 16 | 0;
          return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(length);
        });
      }
    }
  );
}
